import { Card, Flex, Layout, Spin, Typography } from "antd"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useParams } from "react-router"
import { useLocation, useNavigate } from "react-router-dom"

import Images from "../Images"
import AddEmailForm from "../components/Forms/AddEmailForm"
import SignUpEmailForm from "../components/Forms/SignUpEmailForm"
import PrivacyPolicy from "../components/PrivacyPolicy"
import { generateDocumentTitle } from "../constants/document-title"
import { RoutesPaths } from "../constants/routes"
import { type RootState } from "../store"
import { pickFormError } from "../utils/handlers/pick-form-error"
import { useQuery } from "../utils/utils"
import { authErrorReset } from "./LoginPage/redux/auth.slice"
import { createAccount } from "./LoginPage/redux/auth.thunks"
import { fetchInviteTeamMemberInfo } from "./TeamManagementPage/redux/team-members.thunks"

const { Title, Paragraph } = Typography

type SignUpFormData = Record<string, unknown>

function SignUpPage({
  //isMobile,
  auth: {
    error,
    fetching,
    userInfo
    //isAdmin
  },
  inviteInfoFetching,
  inviteInfo,
  createAccount,
  getInviteUserInfo,
  resetError
}: ConnectedProps<typeof connector>) {
  const { search, state } = useLocation()
  const query = useQuery(search)
  const invite = query.get("invite")

  const navigate = useNavigate()
  const { hash } = useParams()
  const { t } = useTranslation()

  const initialValues =
    !!userInfo && state === "change_email"
      ? {
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          email: userInfo.email
        }
      : null

  function handleSubmit(data: SignUpFormData) {
    createAccount({
      data: {
        ...data,
        ...(invite && { invite })
      },
      navigate
    })
  }

  useEffect(() => {
    if (hash) localStorage.setItem("ref_link_info", hash)
    if (invite) getInviteUserInfo(invite)
    if (userInfo) {
      if (userInfo?.["email_added"]) {
        navigate(RoutesPaths.home + (search || ""))
      } else {
        if (userInfo?.["email_added"]) {
          if (userInfo?.["is_email_verified"]) {
            navigate("/onboarding/business-info")
          } else {
            if (initialValues) return
            navigate("/check-mailbox")
          }
        } else {
          console.log("add-email")
          navigate("/add-email")
        }
      }
    } else {
      generateDocumentTitle("Sign Up")
    }
  }, [])

  useEffect(() => {
    if (error) resetError()
  }, [error])

  return (
    <Layout className={"min-h-svh auth-bg"}>
      <Spin size="large" spinning={fetching || inviteInfoFetching}>
        <Flex
          vertical
          align="center"
          justify="center"
          className={"min-h-svh md:pt-14"}
        >
          {/*<LoginHeader />*/}

          <Card
            className="login-card shadow-sm border-gray-200/70"
            style={{ margin: "auto", width: "100%", maxWidth: "520px" }}
          >
            <Flex vertical align="center" style={{ marginBottom: 16 }}>
              <div className={"flex items-center gap-4"}>
                <img src={Images.logo} alt="Logo" className="h-16 md:h-20" />
                {/*<Flex vertical>*/}
                {/*  <Text className="font-semibold text-2xl md:text-4xl !leading-relaxed">*/}
                {/*    EZBuild*/}
                {/*  </Text>*/}
                {/*  <Text className={"text-sm md:text-lg font-semibold"}>*/}
                {/*    Simple. Easy. Smart.*/}
                {/*  </Text>*/}
                {/*</Flex>*/}
              </div>
              {!userInfo?.["email_added"] && Boolean(userInfo) ? (
                <>
                  <Title level={1} style={{ marginBottom: 6 }}>
                    {t("Enter Your Email")}
                  </Title>
                  <AddEmailForm
                    onSubmit={handleSubmit}
                    error={error?.message}
                  />
                </>
              ) : (
                <>
                  <Title level={1} style={{ marginBottom: 6 }}>
                    {t("Create an account")}
                  </Title>
                  <Paragraph
                    type="secondary"
                    className={"text-center"}
                    style={{ fontSize: 16, margin: 0 }}
                  >
                    {t("Let’s set up your EZBuild account.")}
                  </Paragraph>

                  <div className={"pt-4 w-full"}>
                    <SignUpEmailForm
                      {...(initialValues && { initialValues })}
                      {...(invite && inviteInfo && { inviteInfo })}
                      isInvite={!!invite}
                      onSubmit={handleSubmit}
                      error={pickFormError(error)}
                    />
                  </div>
                </>
              )}
            </Flex>
          </Card>

          <PrivacyPolicy />
        </Flex>
      </Spin>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  isMobile: state.nav.isMobile,
  inviteInfoFetching: state.users.inviteInfoFetching,
  inviteInfo: state.users.inviteInfo
})

// wraps dispatch to create nicer functions to call within our component
const mapDispatchToProps = {
  createAccount: createAccount,
  resetError: authErrorReset,
  getInviteUserInfo: fetchInviteTeamMemberInfo
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SignUpPage)
